/* eslint-disable no-restricted-syntax */
// Double cast in this file safe
import {isFeatureEnabled} from 'feature-flags';
import z, {ZodNullable, ZodObject, ZodOptional, ZodRawShape, ZodTypeAny} from 'zod';

import {useCallback} from 'react';
import {useLocation} from 'react-router-dom';

import {uniq} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {useGetStageSequenceMandatoryFieldsListQuery} from '@dms/api/settings';
import {ConditionEnum} from '@dms/api/vehicle';
import {featureFlags} from '@dms/feature-flags';

import {COUNTRY_CZE} from '../../constants/country';
import {FieldAvailabilityContext, mandatoryFieldsMap} from '../../utils/mandatoryFieldsMap';
import {useTenant} from '../useTenant';
import {useShouldDisableMandatoryFields} from './hooks/useShouldDisableMandatoryFields';
import {makeFieldRequired} from './utils/makeFieldRequired';

export type {FieldAvailabilityContext};

function isFieldAvailableInContext(formField: string, context: FieldAvailabilityContext): boolean {
  const entry = Object.values(mandatoryFieldsMap).find((f) => f.formField === formField);
  if (!entry?.isAvailableFor) {
    return true; // no constraint = available in all contexts
  }
  return entry.isAvailableFor(context);
}

export function useMandatoryFields() {
  const location = useLocation();
  const {isTenantCountrySupported} = useTenant();
  const shouldSkipMandatoryFieldsValidation = useShouldDisableMandatoryFields();

  const {data: mandatorySS, isLoading} = useGetStageSequenceMandatoryFieldsListQuery({
    definitionId: 'SALES',
  });

  const extractMandatoryFields = useCallback(
    (filterStageId?: string, context: FieldAvailabilityContext = {}) => {
      if (isNilOrEmpty(mandatorySS)) {
        return [];
      }

      const fields = mandatorySS?.stageSequences.flatMap((ss) =>
        ss.mandatoryFields
          .filter((f) => !filterStageId || f.stageId === filterStageId)
          .map((f) => f.fieldId)
      );

      return uniq(
        (fields ?? [])
          .map(
            (field) => mandatoryFieldsMap[field as keyof typeof mandatoryFieldsMap]?.formField ?? ''
          )
          .filter(Boolean)
          .filter((formField) => isFieldAvailableInContext(formField, context))
      );
    },
    [mandatorySS]
  );

  const getProcessedMandatoryFields = useCallback(
    (context: FieldAvailabilityContext = {}) => extractMandatoryFields(undefined, context),
    [extractMandatoryFields]
  );

  const getMandatoryFieldsBeforeVehicleCreate = useCallback(
    (context: FieldAvailabilityContext = {}) => extractMandatoryFields('ON_CREATION', context),
    [extractMandatoryFields]
  );

  const isMandatoryFieldsEnabled = useCallback(
    (condition?: ConditionEnum, context: FieldAvailabilityContext = {}) => {
      if (shouldSkipMandatoryFieldsValidation) {
        return false;
      }

      if (isTenantCountrySupported(COUNTRY_CZE) && condition === 'CONDITION_NEW') {
        return false;
      }

      return (
        isFeatureEnabled(featureFlags.CONFIGURABLE_MANDATORY_FIELDS) &&
        !isNilOrEmpty(getProcessedMandatoryFields(context))
      );
    },
    [getProcessedMandatoryFields, isTenantCountrySupported, shouldSkipMandatoryFieldsValidation]
  );

  const getMandatoryFields = useCallback(
    (context: FieldAvailabilityContext = {}) => {
      if (shouldSkipMandatoryFieldsValidation) {
        return [];
      }

      if (location.pathname.includes('/rental-vehicles/v2')) {
        return [];
      }

      return getProcessedMandatoryFields(context);
    },
    [getProcessedMandatoryFields, location.pathname, shouldSkipMandatoryFieldsValidation]
  );

  const generateSchema = useCallback(
    <T extends ZodRawShape>(
      schema: ZodObject<T, 'strip', ZodTypeAny>,
      context: FieldAvailabilityContext = {},
      condition?: ConditionEnum
    ) => {
      if (shouldSkipMandatoryFieldsValidation) {
        return schema;
      }

      if (
        isNilOrEmpty(mandatorySS) ||
        !isFeatureEnabled(featureFlags.CONFIGURABLE_MANDATORY_FIELDS) ||
        (condition === 'CONDITION_NEW' && isTenantCountrySupported(COUNTRY_CZE))
      ) {
        return schema;
      }

      const requiredFieldNames = getMandatoryFieldsBeforeVehicleCreate(context);

      if (isNilOrEmpty(requiredFieldNames)) {
        return schema;
      }

      const formShape = schema.shape;
      const newShape = {} as T;

      for (const key in formShape) {
        const typedKey = key as keyof T;
        const field = formShape[typedKey];
        const isMandatoryField = requiredFieldNames.includes(key);

        const hasNestedMandatoryFields = requiredFieldNames.some((fieldPath) =>
          fieldPath.startsWith(`${key}.`)
        );

        if (!isMandatoryField && !hasNestedMandatoryFields) {
          newShape[typedKey] = field as T[keyof T];
          continue;
        }

        if (isMandatoryField) {
          if (field.isOptional()) {
            newShape[typedKey] = makeFieldRequired(field) as T[keyof T];
          } else {
            newShape[typedKey] = field as T[keyof T];
          }
        } else if (
          hasNestedMandatoryFields &&
          (field instanceof ZodObject ||
            (field instanceof ZodOptional && field.unwrap() instanceof ZodObject) ||
            (field instanceof ZodNullable && field.unwrap() instanceof ZodObject) ||
            (field instanceof ZodOptional &&
              field.unwrap() instanceof ZodNullable &&
              field.unwrap().unwrap() instanceof ZodObject))
        ) {
          let objectToProcess;
          let isOptional = false;
          let isNullable = false;

          if (field instanceof ZodObject) {
            objectToProcess = field;
          } else if (field instanceof ZodOptional && field.unwrap() instanceof ZodObject) {
            objectToProcess = field.unwrap();
            isOptional = true;
          } else if (field instanceof ZodNullable && field.unwrap() instanceof ZodObject) {
            objectToProcess = field.unwrap();
            isNullable = true;
          } else if (
            field instanceof ZodOptional &&
            field.unwrap() instanceof ZodNullable &&
            field.unwrap().unwrap() instanceof ZodObject
          ) {
            objectToProcess = field.unwrap().unwrap();
            isOptional = true;
            isNullable = true;
          } else {
            newShape[typedKey] = field as T[keyof T];
            continue;
          }

          const nestedShape = objectToProcess.shape;
          const newNestedShape: Record<string, ZodTypeAny> = {};

          for (const nestedKey in nestedShape) {
            const nestedField = nestedShape[nestedKey];
            const fullFieldPath = `${key}.${nestedKey}`;

            if (requiredFieldNames.includes(fullFieldPath)) {
              if (nestedField.isOptional()) {
                newNestedShape[nestedKey] = makeFieldRequired(nestedField);
              } else {
                newNestedShape[nestedKey] = nestedField;
              }
            } else {
              newNestedShape[nestedKey] = nestedField;
            }
          }

          const updatedNestedObject = z.object(newNestedShape);

          if (isNullable && isOptional) {
            newShape[typedKey] = updatedNestedObject.nullable().optional() as unknown as T[keyof T];
          } else if (isNullable) {
            newShape[typedKey] = updatedNestedObject.nullable() as unknown as T[keyof T];
          } else if (isOptional) {
            newShape[typedKey] = updatedNestedObject.optional() as unknown as T[keyof T];
          } else {
            newShape[typedKey] = updatedNestedObject as unknown as T[keyof T];
          }
        }
      }

      return z.object(newShape);
    },
    [getMandatoryFieldsBeforeVehicleCreate, mandatorySS, shouldSkipMandatoryFieldsValidation]
  );

  return {
    generateSchema,
    getMandatoryFields,
    isMandatoryFieldsEnabled,
    isLoading,
  };
}
